// src/features/campaigns/components/CampaignPreview.tsx
// ============================================================================
// Re-cableado a lealify-base (Inc10c). El envío es 100% server-side: el worker de
// base drena la cola con jitter/warm-up/topes. Esta vista ya NO envía desde el
// navegador (fuera useWahaSending + Firestore + Elasticsearch). Muestra:
//   · preview del mensaje + editar
//   · tarjeta de envío: programar (SendPlanModal) o, si ya está programado,
//     progreso + controles (pausar/reanudar/cancelar/enviar ya)
//   · monitor de la cola (pendientes/enviados/fallidos) desde /campaigns/:id/queue
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Users, AlertCircle, RefreshCw, Edit2, Filter, Send, CalendarClock, Loader2, Play, Sparkles, Check, HelpCircle, Trash2,
  X, Undo2, UserMinus, Ban, CalendarOff,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Campaign } from '../types/campaign';
import { useAuth } from '../../../context/AuthContext';
import { campaignsApi, type AssistedQuota } from '../../../api/campaignsApi';
import { clientsApi } from '../../../api/clientsApi';
import { motivoDeFreno } from '../campaignRitmo';
import SendPlanModal from './SendPlanModal';
import EmailResultados from './EmailResultados';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import type { SendPlan } from '../types/campaign';

interface CampaignPreviewProps {
  campaign: Campaign;
  loading: boolean;
  onEditMessage: (message: string) => void;
}

type QueueRow = {
  id: number; contact_id: number; phone: string; status: string; attempts: number;
  sent_at: string | null; detail: string | null;
  // El motor los manda desde siempre (LEFT JOIN contacts). Antes se ignoraban y la cola
  // era una lista de teléfonos sueltos: no se sabía A QUIÉN se le estaba escribiendo.
  first_name?: string | null; last_name?: string | null; phone_live?: string | null;
  reason?: string | null;
  // Fatiga (3-sep-2026): el motor lo manda por fila PENDIENTE — «recibió «X» hace N d».
  fatiga_campana?: string | null; fatiga_hace_dias?: number | null; fatiga_canal?: 'whatsapp' | 'email' | null;
};
type QueueFilter = 'all' | 'pending' | 'sent' | 'failed' | 'discarded';

const QUEUE_FILTERS: { value: QueueFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'sent', label: 'Enviados' },
  { value: 'failed', label: 'Fallidos' },
  // Los descartados NO están en la cola: son un suppress permanente aparte que sobrevive a
  // los ciclos. El endpoint existía y la agenda ya los mostraba; aquí faltaban.
  { value: 'discarded', label: 'Descartados' },
];

/** "Ana López" · "Contacto 412" si el contacto ya no tiene nombre. */
const nombreDe = (r: { first_name?: string | null; last_name?: string | null; contact_id: number }) =>
  `${r.first_name || ''} ${r.last_name || ''}`.trim() || `Contacto ${r.contact_id}`;

const PLAN_ACTIVE = ['scheduled', 'running', 'paused', 'paused_error'];

/**
 * Parte el mensaje del gate anti-ban en cuerpo + nota técnica. El motor manda
 * "Protección anti-ban: <explicación> · <rastro>": el rastro ("sin ninguna señal de relación
 * en nuestro registro") es diagnóstico, va en letra chica; la explicación es lo que el dueño
 * necesita leer. El prefijo se quita porque ya es el título del diálogo.
 */
const partirMensajeGate = (msg: string) => {
  const limpio = String(msg || '').replace(/^Protección anti-ban:\s*/i, '');
  const i = limpio.lastIndexOf(' · ');
  return i > 0
    ? { cuerpo: limpio.slice(0, i).trim(), nota: limpio.slice(i + 3).trim() }
    : { cuerpo: limpio.trim(), nota: null };
};

const CampaignPreview: React.FC<CampaignPreviewProps> = ({ campaign, loading: parentLoading, onEditMessage }) => {
  const { currentUser } = useAuth();

  const [plan, setPlan] = useState<SendPlan | null>(campaign.sendPlan || null);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);
  const [audienceSinWa, setAudienceSinWa] = useState(0);   // excluidos por no tener WhatsApp (F2 check-exists)
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [busy, setBusy] = useState(false);

  // Variaciones anti-ban: pool previsualizado + las que están realmente aplicadas/guardadas.
  const [varPreview, setVarPreview] = useState<string[] | null>(null);
  const [varLoading, setVarLoading] = useState(false);
  const [varApplied, setVarApplied] = useState(false);          // ¿la propuesta en pantalla ya se aplicó?
  const [showCurrent, setShowCurrent] = useState(false);        // desplegar las variaciones vigentes
  const [currentVariations, setCurrentVariations] = useState<string[]>(campaign.variations || []);
  const approvedCount = currentVariations.length;
  // Toggle ON/OFF de toda la función + edición individual de las vigentes.
  const [varyOn, setVaryOn] = useState<boolean>(campaign.varyMessage !== false);
  const [varyToggling, setVaryToggling] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editList, setEditList] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('all');

  // Aviso del gate anti-ban con el theme del manager (antes: window.confirm nativo, la cajita
  // gris del SO fuera de diseño). Guarda la fila que se intentaba mandar para reintentarla
  // con force si el dueño decide saltarse la protección.
  const [gateAsk, setGateAsk] = useState<{ queueId: number; contactId?: number; message: string } | null>(null);

  const planIsActive = !!plan && PLAN_ACTIVE.includes(plan.status);
  // Canal correo: una solicitud programada sin aprobar está EN REVISIÓN (no sale hasta que la plataforma la aprueba).
  const isEmail = campaign.channel === 'email';
  const inReview = isEmail && !campaign.approvedAt;

  // Mensaje de preview con variables resueltas a marcadores legibles.
  const previewMessage = useCallback(() => {
    let m = campaign.message || '';
    m = m.replace(/@nombre/g, '[Nombre]').replace(/@puntos/g, '[Puntos]');
    m = m.replace(/@marca/g, currentUser?.businessName || '[Tu negocio]');
    return m;
  }, [campaign.message, currentUser?.businessName]);

  // Refresca la campaña (estado del plan + conteos) desde base.
  const refresh = useCallback(async () => {
    try {
      const c = await campaignsApi.get(campaign.id);
      if (c) setPlan(c.sendPlan || null);
    } catch { /* la tarjeta queda con el último estado conocido */ }
  }, [campaign.id]);

  // ── Audiencia de un BORRADOR: el CUÁNTOS y también el QUIÉNES (24-jul-2026) ──
  // La cola es la única lista con nombres y nace al programar, así que hasta hoy un
  // borrador era un número a ciegas: «se enviará a 68 clientes» y ninguna forma de saber
  // cuáles. Se carga bajo demanda (al desplegar) porque resuelve la audiencia entera.
  // Conteo de audiencia (solo borrador / sin programar, para mostrar a cuántos irá).
  useEffect(() => {
    let alive = true;
    if (campaign.sendPlan && PLAN_ACTIVE.includes(campaign.sendPlan.status)) { setAudienceCount(null); return; }
    (async () => {
      try { const n = await campaignsApi.previewCampaign(campaign.id); if (alive) setAudienceCount(n); }
      catch { if (alive) setAudienceCount(null); }
    })();
    return () => { alive = false; };
  }, [campaign.id, campaign.sendPlan]);

  // ── Envío ASISTIDO (24-jul-2026) ───────────────────────────────────────────
  // Antes el manager no sabía siquiera que existía: TODAS sus campañas nacían
  // automáticas. Un negocio con el WhatsApp recién conectado —Cakery— no tenía forma
  // de soltar los mensajes de a uno mientras ve cómo responde el número.
  const esAsistida = campaign.dispatch === 'assisted';
  const [quota, setQuota] = useState<AssistedQuota | null>(null);
  const [enviandoId, setEnviandoId] = useState<number | null>(null);

  // El cupo se consulta en TODA campaña de WhatsApp con cola, no solo en las asistidas:
  // desde el 24-jul cualquiera puede mandar una fila suelta a mano, así que hace falta
  // saber si queda cupo para habilitar o no el botón.
  const cargarCupo = useCallback(async () => {
    if (isEmail) return;
    try { setQuota(await campaignsApi.quota(campaign.id)); } catch { /* informativo */ }
  }, [isEmail, campaign.id]);

  useEffect(() => { cargarCupo(); }, [cargarCupo]);

  // ── Descartar / deshacer, como en la agenda (24-jul-2026) ─────────────────────
  // El manager perdió esto en Inc10c. Son DOS cosas distintas y por eso hay modal:
  //   · «Solo esta campaña» → suppress de la campaña (campaign_contact_suppress).
  //   · «Bloquear siempre»  → contacts.marketing_opt_out = 1: sale de TODA audiencia.
  const [aDescartar, setADescartar] = useState<QueueRow | null>(null);

  const confirmarDescarte = async (permanente: boolean) => {
    const fila = aDescartar;
    if (!fila) return;
    setADescartar(null);
    try {
      await campaignsApi.discard(campaign.id, fila.contact_id, permanente ? 'bloqueado de marketing' : null as any);
      if (permanente) await clientsApi.setMarketingOptOut(String(fila.contact_id), true);
      toast.success(permanente ? 'Bloqueado de todas las campañas.' : 'Descartado de esta campaña.');
      await loadQueue();
    } catch (e: any) { toast.error(e?.message || 'No se pudo descartar.'); }
  };

  const deshacer = async (fila: QueueRow) => {
    try {
      if (fila.status === 'discarded') {
        await campaignsApi.undiscard(campaign.id, fila.contact_id);
        // Si además se había bloqueado de marketing, se levanta: si no, «deshacer» no
        // devolvería a la persona a la audiencia y parecería que no hizo nada.
        await clientsApi.setMarketingOptOut(String(fila.contact_id), false).catch(() => {});
        toast.success('Vuelve a entrar en la campaña.');
      } else {
        await campaignsApi.unmarkSent(campaign.id, fila.contact_id);
        toast.success('Marcado como pendiente otra vez.');
      }
      await loadQueue(); await refresh();
    } catch (e: any) { toast.error(e?.message || 'No se pudo deshacer.'); }
  };

  // `fila.id` negativo = la campaña no está programada y esa fila de cola todavía no
  // existe; el servidor la crea a partir del contacto. Positivo = fila real de la cola.
  const enviarFila = async (queueId: number, contactId?: number, force = false) => {
    setEnviandoId(queueId);
    try {
      const r = queueId > 0
        ? await campaignsApi.sendRow(campaign.id, queueId, force)
        : await campaignsApi.sendContact(campaign.id, Number(contactId), force);
      if (r.quota) setQuota(r.quota);
      toast.success(r.shadow ? 'Registrado (sin WhatsApp conectado)' : '¡Enviado!');
      await loadQueue(); await refresh();
    } catch (e: any) {
      // Protección anti-ban (gate F3, code GATED): advertir y dejar decidir. El
      // override sale por el MOTOR (contabilizado y auditado), nunca por otra vía.
      const d = e?.data || {};
      if (d.code === 'GATED') {
        setEnviandoId(null);
        // Overridable = freno de FRÍO: se puede saltar, pero es decisión consciente y se
        // pregunta con el diálogo del manager. No overridable (sesión caída, 463 vivo) no se
        // negocia: ahí enviar es dañar el número.
        if (d.overridable) { setGateAsk({ queueId, contactId, message: e.message }); return; }
        toast.error(e.message);
        return;
      }
      // El motor explica en español por qué frenó (hora, cupo del número, reserva de
      // cumpleaños…). No se reintenta por otra vía: saltarse el tope es lo que quema el número.
      // El 429 trae el contador ya actualizado; BaseApiError lo deja en `.data`.
      const q = e?.data?.quota ?? e?.quota;
      if (q) setQuota(q);
      toast.error(e?.message || 'No se pudo enviar.');
      await cargarCupo();
    } finally { setEnviandoId(null); }
  };

  // ── AUDIENCIA: una sola lista, exista o no cola todavía (24-jul-2026) ──────────
  // Antes esto era un "monitor de la cola" que solo se dibujaba con la campaña ya
  // programada. Como casi todas las campañas de lealtad son BORRADORES, el negocio abría
  // la suya y no veía a nadie: ni cuántos, ni quiénes, ni descartados. Y antes de Inc10c
  // (25-jun) esa lista salía siempre, porque el envío se hacía desde el navegador.
  //
  // Ahora hay UNA fuente por estado de la campaña:
  //   · programada  → la cola real (pending/sent/failed) + los descartados
  //   · borrador    → la audiencia que resolvería el motor hoy, como "pendientes",
  //                   + los descartados (que ya se pueden marcar antes de programar)
  // Se carga TODO de una vez y se filtra en el navegador: así cada pestaña puede
  // llevar su conteo, que es justo lo que se quiere ver de un vistazo.
  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const [filas, desc] = await Promise.all([
        planIsActive
          ? campaignsApi.queue(campaign.id)
          : campaignsApi.audience(campaign.id, 1000).then((r) => {
              setAudienceCount(r.total);
              setAudienceSinWa(Number(r.sin_whatsapp || 0));
              // id NEGATIVO = fila que todavía no existe en la cola. Se usa para saber que
              // al pulsar «Enviar» hay que crearla (send-contact) en vez de enviar por id.
              // Quien ya recibió NO vuelve a listarse como pendiente: si no, el dedup
              // (pendiente gana sobre enviado) los enseñaría a todos como pendientes y
              // «Enviados» volvería a decir 0, que es justo el problema que se arregla.
              const yaRecibieron = new Set((r.history || []).map((h) => Number(h.contact_id)));
              const previstos = r.contacts.filter((c) => !yaRecibieron.has(Number(c.contact_id))).map((c, i) => ({
                id: -(1000 + i), contact_id: c.contact_id, phone: c.phone || '',
                status: 'pending', attempts: 0, sent_at: null, detail: null,
                first_name: c.first_name, last_name: c.last_name,
              })) as QueueRow[];
              // Los que YA recibieron: histórico rescatado del sistema anterior o mandados
              // a mano. No están en la cola y sin esto la campaña parecía no haber salido.
              const historicos = (r.history || []).map((h, i) => ({
                id: -(50000 + i), contact_id: h.contact_id, phone: h.phone || '',
                status: 'sent', attempts: 0, sent_at: h.sent_at || null,
                detail: h.origen === 'legacy' ? 'histórico' : null,
                first_name: h.first_name, last_name: h.last_name,
              })) as QueueRow[];
              return [...previstos, ...historicos];
            }),
        campaignsApi.discarded(campaign.id).catch(() => []),
      ]);
      // La cola puede traer VARIAS filas por contacto (reentradas por ciclo) y la lista
      // debe enseñar una persona una vez. Gana el estado que pide acción: pendiente >
      // fallido > enviado > cancelado. Mismo criterio que la agenda.
      const rango: Record<string, number> = { pending: 4, failed: 3, sent: 2, cancelled: 1, skipped: 0 };
      const porContacto = new Map<number, QueueRow>();
      for (const r of filas as QueueRow[]) {
        const prev = porContacto.get(r.contact_id);
        if (!prev || (rango[r.status] ?? 0) > (rango[prev.status] ?? 0)
          || ((rango[r.status] ?? 0) === (rango[prev.status] ?? 0) && r.id > prev.id)) {
          porContacto.set(r.contact_id, r);
        }
      }
      // Los descartados MANDAN sobre lo que diga la cola: si alguien se descartó a mano,
      // su fila pendiente sigue ahí pero ya no se le va a escribir.
      for (const s of desc) {
        porContacto.set(s.contact_id, {
          id: -(porContacto.size + 1), contact_id: s.contact_id, phone: s.phone,
          status: 'discarded', attempts: 0, sent_at: null, detail: null,
          first_name: s.first_name, last_name: s.last_name, reason: s.reason || null,
        });
      }
      setQueue([...porContacto.values()]);
    } catch { setQueue([]); }
    finally { setQueueLoading(false); }
  }, [campaign.id, planIsActive]);

  useEffect(() => {
    loadQueue();
    const running = plan?.status === 'running' || plan?.status === 'scheduled';
    if (!running) return;
    const t = setInterval(() => { loadQueue(); refresh(); }, 15000);
    return () => clearInterval(t);
  }, [plan?.status, loadQueue, refresh]);

  // Conteos por pestaña + la lista ya filtrada. En el navegador: la lista completa ya
  // está cargada y así cada pestaña puede decir cuántos lleva.
  const conteos = React.useMemo(() => {
    const c = { all: queue.length, pending: 0, sent: 0, failed: 0, discarded: 0 } as Record<QueueFilter, number>;
    for (const q of queue) if (q.status in c) c[q.status as QueueFilter]++;
    return c;
  }, [queue]);
  // Por qué NO se puede mandar de a uno ahora mismo, o null si sí se puede. El motor
  // rechaza pausada/archivada (`assertEnvioActivo`): mejor decirlo antes que dejar pulsar
  // un botón que siempre va a fallar.
  const envioBloqueado = (plan?.status === 'paused' || plan?.status === 'paused_error')
    ? 'El envío está pausado: reanúdalo para poder mandar.'
    : campaign.archivedAt ? 'La campaña está archivada.' : null;

  const queueVisible = React.useMemo(
    () => (queueFilter === 'all' ? queue : queue.filter((q) => q.status === queueFilter)),
    [queue, queueFilter]);

  // ── Controles del plan ──
  const doAction = async (fn: () => Promise<any>, okMsg: string) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); await refresh(); await loadQueue(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo completar la acción.'); }
    finally { setBusy(false); }
  };
  const handlePause = () => doAction(() => campaignsApi.pause(campaign.id), 'Envío pausado.');
  const handleResume = () => doAction(() => campaignsApi.resume(campaign.id), 'Envío reanudado.');
  const handleRunNow = () => doAction(() => campaignsApi.runNow(campaign.id), 'Drenando el cupo de hoy…');
  // Cancelar es IRREVERSIBLE (el motor pone la cola entera en 'cancelled'): se pregunta con el
  // diálogo del manager, no con el confirm nativo, y se dice qué pasa con lo ya enviado.
  const [askCancel, setAskCancel] = useState(false);
  const handleCancel = () => setAskCancel(true);

  // Muestra las variaciones legibles (con los placeholders resueltos, igual que el preview).
  const prettyVar = (t: string) =>
    t.replace(/@nombre/g, '[Nombre]').replace(/@puntos/g, '[Puntos]').replace(/@marca/g, currentUser?.businessName || '[Tu negocio]');

  const handlePreviewVariations = async () => {
    setVarLoading(true);
    try {
      const r = await campaignsApi.previewVariations(campaign.id, 12);
      setVarPreview(r.variations || []);
      setVarApplied(false);   // propuesta nueva = aún sin aplicar
      if ((r.variations?.length || 0) < 2) toast('Gemini no generó variaciones esta vez, intenta "Generar otras".', { icon: '⚠️' });
    } catch (e: any) {
      toast.error(e?.message || 'No se pudieron generar variaciones.');
    } finally { setVarLoading(false); }
  };

  const handleApproveVariations = async () => {
    if (!varPreview || !varPreview.length) return;
    setVarLoading(true);
    try {
      const r = await campaignsApi.approveVariations(campaign.id, varPreview);
      setCurrentVariations(varPreview);
      setVarApplied(true);
      toast.success(`${r.saved} variaciones aprobadas — se usarán al enviar.`);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudieron aprobar las variaciones.');
    } finally { setVarLoading(false); }
  };

  // Campaña ya lanzada: aplica las variaciones a los mensajes que aún no se han enviado.
  const handleApplyPending = async () => {
    if (!varPreview || !varPreview.length) return;
    setVarLoading(true);
    try {
      const r = await campaignsApi.applyVariationsToPending(campaign.id, varPreview);
      setCurrentVariations(varPreview);
      setVarApplied(true);
      toast.success(`Variaciones aplicadas a ${r.updated} mensajes pendientes.`);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudieron aplicar a los pendientes.');
    } finally { setVarLoading(false); }
  };

  // Activa/desactiva TODA la función de variaciones (envía solo el original si OFF).
  const handleToggleVary = async () => {
    const next = !varyOn;
    setVaryToggling(true);
    try {
      await campaignsApi.setVaryMessage(campaign.id, next);
      setVaryOn(next);
      toast.success(next ? 'Variaciones activadas.' : 'Variaciones desactivadas — se enviará solo tu mensaje original.');
    } catch (e: any) {
      toast.error(e?.message || 'No se pudo cambiar el estado.');
    } finally { setVaryToggling(false); }
  };

  // Edición individual de las variaciones vigentes (reescribir / quitar).
  const startEdit = () => { setEditList([...currentVariations]); setEditMode(true); setShowCurrent(true); };
  const cancelEdit = () => { setEditMode(false); setEditList([]); };
  const updateEditItem = (i: number, val: string) => setEditList((l) => l.map((x, idx) => (idx === i ? val : x)));
  const removeEditItem = (i: number) => setEditList((l) => l.filter((_, idx) => idx !== i));

  const saveEdit = async () => {
    const cleaned = editList.map((s) => s.trim()).filter(Boolean);
    if (!cleaned.length) { toast.error('Deja al menos una variación.'); return; }
    setSavingEdit(true);
    try {
      const savedCount = planIsActive
        ? (await campaignsApi.applyVariationsToPending(campaign.id, cleaned)).pool_size
        : (await campaignsApi.approveVariations(campaign.id, cleaned)).saved;
      // Re-leo la campaña para reflejar EXACTAMENTE lo que sobrevivió a la validación.
      const fresh = await campaignsApi.get(campaign.id);
      setCurrentVariations(fresh?.variations || []);
      const rejected = cleaned.length - savedCount;
      if (rejected > 0) {
        toast(`Se guardaron ${savedCount}. ${rejected} quedó descartada por el candado (cambiaste un placeholder, o metiste un número/enlace nuevo).`, { icon: '⚠️', duration: 7000 });
      } else {
        toast.success(planIsActive ? `${savedCount} variaciones aplicadas a los pendientes.` : `${savedCount} variaciones guardadas.`);
      }
      setEditMode(false);
    } catch (e: any) {
      toast.error(e?.message || 'No se pudieron guardar los cambios.');
    } finally { setSavingEdit(false); }
  };

  const sentCount = plan ? plan.totalSent : 0;
  const queuedCount = plan ? plan.totalQueued : 0;
  const pct = queuedCount ? Math.min(100, Math.round((sentCount / queuedCount) * 100)) : 0;

  return (
    <div className="space-y-6 relative">
      {/* ── Mensaje de la campaña ── */}
      <div className="bg-surface shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:px-6 border-b border-line flex justify-between items-center">
          <h3 className="text-lg font-medium text-content">Mensaje de la campaña</h3>
          <button
            onClick={() => onEditMessage(campaign.message)}
            disabled={parentLoading || planIsActive}
            title={planIsActive ? 'Cancela el envío programado para editar' : ''}
            className="inline-flex items-center px-3 py-1.5 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50"
          >
            <Edit2 className="h-4 w-4 mr-2" /> Editar Campaña y Mensaje
          </button>
        </div>
        <div className="px-4 py-5 sm:p-6">
          <div className="bg-sky-50 border border-sky-200 p-4 rounded-lg text-sm text-sky-800 whitespace-pre-line shadow">
            {previewMessage()}
          </div>
          <p className="mt-2 text-xs text-content-3">
            * El mensaje se personaliza con el nombre y puntos de cada cliente al enviarse.
          </p>
        </div>
      </div>

      {/* ── Resultados del correo (aperturas/clics) — solo campañas de correo con envíos.
          El componente se auto-oculta si aún no salió nada; el gate isEmail evita pedir
          métricas para campañas de WhatsApp. */}
      {isEmail && sentCount > 0 && <EmailResultados campaignId={campaign.id} size="full" />}

      {/* ── Variaciones del mensaje (anti-ban) ── */}
      <div className="bg-surface shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 sm:px-6 border-b border-line">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-medium text-content flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" /> Variaciones del mensaje
                <span
                  className="text-content-3 cursor-help"
                  title="Para evitar que WhatsApp marque el envío como spam, cada cliente recibe el MISMO mensaje con la redacción ligeramente distinta (misma oferta, solo cambian palabras). La IA las escribe; tú las revisas y las aplicas."
                >
                  <HelpCircle className="h-4 w-4" />
                </span>
              </h3>
              <p className="mt-1 text-xs text-content-3">
                {varyOn
                  ? <>Son <strong>2 pasos</strong>: <strong>1)</strong> generas una propuesta (la IA redacta variantes — es normal que cambien cada vez) y <strong>2)</strong> la <strong>{planIsActive ? 'aplicas' : 'apruebas'}</strong>. Nada se guarda hasta ese segundo paso.</>
                  : <>Desactivadas: cada cliente recibe tu mensaje original tal cual.</>}
              </p>
            </div>
            {/* Toggle ON/OFF de toda la función */}
            <button
              onClick={handleToggleVary}
              disabled={varyToggling}
              role="switch"
              aria-checked={varyOn}
              title={varyOn ? 'Desactivar variaciones (enviar solo el mensaje original)' : 'Activar variaciones anti-bloqueo'}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${varyOn ? 'bg-accent' : 'bg-surface-2 border border-line'}`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${varyOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>

        {varyOn && (
          <div className="px-4 py-4 sm:p-6 space-y-4">

            {/* Estado actual: qué hay REALMENTE aplicado/guardado ahora mismo (editable) */}
            <div className={`rounded-md border px-3 py-2 text-sm ${approvedCount > 0 ? 'border-emerald-200 bg-emerald-50 dark:bg-emerald-500/10' : 'border-line bg-surface-2'}`}>
              {approvedCount > 0 ? (
                <div className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-emerald-800 dark:text-emerald-300 flex items-center gap-1 font-medium">
                      <Check className="h-4 w-4" />
                      {approvedCount} variaciones {planIsActive ? 'aplicadas a los pendientes' : 'aprobadas (se usarán al enviar)'}
                    </span>
                    <div className="flex items-center gap-3">
                      {!editMode && currentVariations.length > 0 && (
                        <button onClick={startEdit}
                          className="text-xs text-emerald-700 dark:text-emerald-400 underline whitespace-nowrap inline-flex items-center gap-1">
                          <Edit2 className="h-3 w-3" /> Editar
                        </button>
                      )}
                      {currentVariations.length > 0 && (
                        <button
                          onClick={() => { if (editMode) { cancelEdit(); } else { setShowCurrent((v) => !v); } }}
                          className="text-xs text-emerald-700 dark:text-emerald-400 underline whitespace-nowrap"
                        >
                          {editMode || showCurrent ? 'Ocultar' : 'Ver las actuales'}
                        </button>
                      )}
                    </div>
                  </div>

                  {editMode ? (
                    <div className="space-y-2 mt-2">
                      {editList.map((v, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-content-3 text-xs pt-2 w-4 text-right">{i + 1}.</span>
                          <textarea
                            value={v}
                            onChange={(e) => updateEditItem(i, e.target.value)}
                            rows={2}
                            className="flex-1 text-sm rounded-md border border-line bg-surface p-2 text-content resize-y"
                          />
                          <button onClick={() => removeEditItem(i)} title="Quitar esta variación"
                            className="p-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 mt-0.5">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <p className="text-[11px] text-content-3">
                        Puedes usar <code>@nombre</code>, <code>@marca</code> y <code>@puntos</code>. No cambies precios ni agregues enlaces nuevos: esas se descartan al guardar.
                      </p>
                      <div className="flex items-center gap-2">
                        <button onClick={saveEdit} disabled={savingEdit || !editList.some((s) => s.trim())}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-accent hover:opacity-90 disabled:opacity-50">
                          {savingEdit ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                          {planIsActive ? 'Guardar y aplicar a pendientes' : 'Guardar cambios'}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit}
                          className="px-3 py-1.5 text-sm rounded-md border border-line text-content-2 hover:bg-surface-2 disabled:opacity-50">
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    showCurrent && (
                      <ul className="space-y-1 max-h-60 overflow-y-auto mt-2">
                        {currentVariations.map((v, i) => (
                          <li key={i} className="text-xs text-content bg-surface border border-line rounded p-1.5 whitespace-pre-line">
                            <span className="text-content-3 mr-1">{i + 1}.</span>{prettyVar(v)}
                          </li>
                        ))}
                      </ul>
                    )
                  )}
                </div>
              ) : (
                <span className="text-content-2">
                  Aún no hay variaciones {planIsActive ? 'aplicadas' : 'aprobadas'}.{' '}
                  {planIsActive ? 'Los pendientes salen con el texto original.' : 'Si no apruebas, se generan solas al enviar.'}
                </span>
              )}
            </div>

            {/* Paso 1: generar una propuesta nueva (no guarda) */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-content-2">
                {varPreview ? 'Propuesta nueva (sin aplicar todavía):' : 'Genera una propuesta para revisarla.'}
              </span>
              <button
                onClick={handlePreviewVariations}
                disabled={varLoading}
                title="La IA redacta un juego nuevo de variantes. Es normal que cambien cada vez. No se guarda ni aplica nada todavía."
                className="inline-flex items-center px-3 py-1.5 border border-line shadow-sm text-sm font-medium rounded-md text-content-2 bg-surface hover:bg-surface-2 disabled:opacity-50"
              >
                {varLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                {varPreview ? 'Generar otras' : 'Generar variaciones'}
              </button>
            </div>

            {/* Paso 2: aplicar/aprobar la propuesta en pantalla */}
            {varPreview && (
              <>
                <ul className="space-y-2 max-h-80 overflow-y-auto">
                  {varPreview.map((v, i) => (
                    <li key={i} className="text-sm text-content bg-surface-2 border border-line rounded-md p-2 whitespace-pre-line">
                      <span className="text-content-3 mr-1">{i + 1}.</span>{prettyVar(v)}
                    </li>
                  ))}
                </ul>
                {varApplied ? (
                  <p className="text-sm text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                    <Check className="h-4 w-4" /> Esta propuesta ya está {planIsActive ? 'aplicada a los pendientes' : 'aprobada'}. Puedes cerrar o generar otras si quieres cambiarla.
                  </p>
                ) : planIsActive ? (
                  <button
                    onClick={handleApplyPending}
                    disabled={varLoading || varPreview.length < 1}
                    title="Reescribe con estas variantes los mensajes que aún NO se han enviado. Los ya enviados no se tocan."
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent hover:opacity-90 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4 mr-2" /> Aplicar a los mensajes pendientes
                  </button>
                ) : (
                  <button
                    onClick={handleApproveVariations}
                    disabled={varLoading || varPreview.length < 1}
                    title="Guarda estas variantes; se repartirán entre tus clientes cuando programes el envío."
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent hover:opacity-90 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4 mr-2" /> Aprobar estas {varPreview.length} variaciones
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Envío (programar / monitorear) ── */}
      <div className="bg-surface shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 sm:px-6">
          {planIsActive && plan ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-base font-medium text-content flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-blue-500" />
                  Envío programado
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    inReview ? 'bg-amber-50 text-amber-700 border-amber-200'
                      : plan.status === 'paused_error' ? 'bg-red-50 text-red-700 border-red-200'
                      : plan.status === 'running' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-sky-50 text-sky-700 border-sky-200'
                  }`}>
                    {inReview ? '🕓 En revisión' : (<>
                      {plan.status === 'scheduled' && 'Programada'}
                      {plan.status === 'running' && 'En curso'}
                      {plan.status === 'paused' && 'Pausada'}
                      {plan.status === 'paused_error' && 'Pausada por error'}
                    </>)}
                  </span>
                </h3>
                <div className="flex items-center gap-2">
                  {(plan.status === 'running' || plan.status === 'scheduled') && (
                    <button onClick={handlePause} disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-md border border-line text-content-2 hover:bg-surface-2 disabled:opacity-50">Pausar</button>
                  )}
                  {(plan.status === 'paused' || plan.status === 'paused_error') && (
                    <button onClick={handleResume} disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">Reanudar</button>
                  )}
                  {!isEmail && (
                    <button onClick={handleRunNow} disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 inline-flex items-center gap-1">
                      <Play className="h-3 w-3" /> Enviar ya
                    </button>
                  )}
                  <button onClick={handleCancel} disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50">Cancelar</button>
                </div>
              </div>
              <p className="text-sm text-content-2">
                {plan.msgsPerDay}/día · {plan.window.start}–{plan.window.end}
              </p>
              <div>
                <div className="flex justify-between text-xs text-content-3 mb-1">
                  <span>{sentCount} de {queuedCount} enviados</span>
                  {plan.totalFailed > 0 && <span className="text-red-600">{plan.totalFailed} fallidos</span>}
                </div>
                <div className="w-full bg-surface-2 rounded-full h-2 overflow-hidden">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              {inReview && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Tu solicitud de campaña por correo está <b>en revisión</b>. La revisamos y te confirmamos antes de enviarla — <b>todavía no se ha enviado nada</b>.
                </p>
              )}
              {plan.status === 'paused_error' && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  Hubo fallos consecutivos (o tu WhatsApp se desconectó). Reanuda cuando esté listo; se reintenta solo en el próximo ciclo.
                </p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-base font-medium text-content flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-blue-500" /> {isEmail ? 'Solicitud de campaña por correo' : 'Envío inteligente'}
                </h3>
                <p className="text-xs text-content-3 mt-1">
                  {isEmail ? (
                    <>Esto es una <b>solicitud</b>: la revisamos y te confirmamos antes de enviarla. Crearla o solicitarla <b>no la envía</b>. Máximo <b>1 campaña de correo al mes</b>.</>
                  ) : (
                    <>Programa el envío disperso en días y horarios para proteger tu número de WhatsApp.</>
                  )}
                  {audienceCount !== null && <> Audiencia actual: <span className="font-semibold text-content-2">{audienceCount}</span> {audienceCount === 1 ? 'cliente' : 'clientes'}.</>}
                  {audienceSinWa > 0 && <span title="Números verificados que no están en WhatsApp: se excluyen para proteger el número. Si esa persona escribe algún día, se reincorpora sola."> Se excluyeron <b>{audienceSinWa}</b> sin WhatsApp.</span>}
                </p>
              </div>
              <button onClick={() => setShowPlanModal(true)}
                className="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
                {isEmail ? <><Send className="h-4 w-4 mr-2" /> Enviar solicitud</> : <><CalendarClock className="h-4 w-4 mr-2" /> Programar envío</>}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de descarte — se recupera el del manager ANTERIOR a Inc10c, con su misma
          redacción e iconos: «Saltar esta campaña» / «Dar de baja permanente». La segunda
          pone contacts.marketing_opt_out = 1, y eso sigue sacando a la persona también de
          cumpleaños y seguimientos (verificado en lib/birthdays.js y lib/nurture.js), así
          que la promesa «ni cumpleaños» que hacía el texto viejo se mantiene. */}
      {aDescartar && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setADescartar(null)} />
          <div className="relative bg-surface rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
            <button onClick={() => setADescartar(null)}
              className="absolute top-3 right-3 text-content-3 hover:text-content-2 transition-colors">
              <X className="h-5 w-5" />
            </button>
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center mb-3">
                <UserMinus className="h-6 w-6 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="text-lg font-semibold text-content">Gestionar envío</h3>
              <p className="text-sm text-content-3 mt-1">{nombreDe(aDescartar)}</p>
            </div>
            <div className="space-y-3">
              <button onClick={() => confirmarDescarte(false)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-line hover:bg-surface-2 transition-colors text-left">
                <CalendarOff className="h-5 w-5 text-content-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-content">Saltar esta campaña</p>
                  <p className="text-xs text-content-3">Aparecerá en futuras campañas</p>
                </div>
              </button>
              <button onClick={() => confirmarDescarte(true)}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-red-200 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors text-left">
                <Ban className="h-5 w-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">Dar de baja permanente</p>
                  <p className="text-xs text-red-600 dark:text-red-400">No recibirá campañas ni cumpleaños</p>
                </div>
              </button>
            </div>
            <div className="text-center pt-1">
              <button onClick={() => setADescartar(null)}
                className="text-xs text-content-3 hover:text-content-2 font-medium px-4 py-1.5 rounded-lg hover:bg-surface-2 transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>, document.body)}

      {showPlanModal && (
        <SendPlanModal
          campaign={campaign}
          onClose={() => setShowPlanModal(false)}
          onScheduled={() => { setShowPlanModal(false); refresh(); }}
        />
      )}

      {/* Protección anti-ban: el motor frenó un envío EN FRÍO y se puede saltar a conciencia.
          Antes salía en un window.confirm nativo (fuera de diseño y con el "¿Enviar de todos
          modos?" pegado al texto); aquí el porqué se lee completo y la decisión es un botón. */}
      <ConfirmDialog
        open={!!gateAsk}
        title="Protección anti-ban"
        icon={<Ban className="h-5 w-5 text-amber-500 mr-2.5 flex-shrink-0" />}
        confirmLabel="Enviar de todos modos"
        cancelLabel="Mejor no"
        message={
          <>
            <p>{partirMensajeGate(gateAsk?.message || '').cuerpo}</p>
            {partirMensajeGate(gateAsk?.message || '').nota && (
              <p className="mt-2 text-xs text-content-3">
                {partirMensajeGate(gateAsk?.message || '').nota}
              </p>
            )}
          </>
        }
        onCancel={() => { setGateAsk(null); toast('No se envió: protegido por el anti-ban. 🛡️'); }}
        onConfirm={() => {
          const g = gateAsk;
          setGateAsk(null);
          if (g) enviarFila(g.queueId, g.contactId, true);
        }}
      />

      {/* Cancelar el envío programado: irreversible, la cola pendiente entera se cancela. */}
      <ConfirmDialog
        open={askCancel}
        busy={busy}
        title="Cancelar el envío programado"
        icon={<CalendarOff className="h-5 w-5 text-amber-500 mr-2.5 flex-shrink-0" />}
        confirmLabel="Sí, cancelar el envío"
        cancelLabel="Volver"
        message={
          <>
            <p>Se cancelan todos los mensajes que faltan por salir de esta campaña.</p>
            <p className="mt-2 text-xs text-content-3">Los que ya se enviaron no se afectan.</p>
          </>
        }
        onCancel={() => setAskCancel(false)}
        onConfirm={() => { setAskCancel(false); doAction(() => campaignsApi.cancel(campaign.id), 'Envío programado cancelado.'); }}
      />

      {/* ── (sigue la audiencia) ── */}

      {/* ── AUDIENCIA — siempre, esté programada o no ────────────────────────────
          Antes esto era "Cola de envío" y solo se dibujaba con `planIsActive`. Como casi
          todas las campañas de lealtad son borradores, el negocio abría la suya y no veía
          a nadie. Ahora en borrador se listan los que RECIBIRÍAN, y una vez programada,
          los de la cola real con su estado. */}
      {!parentLoading && (
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h3 className="text-lg font-medium text-content flex items-center">
              <Users className="h-5 w-5 mr-2 text-content-3" />
              Audiencia{queue.length ? <span className="ml-2 text-sm font-normal text-content-3">({queue.length})</span> : null}
            </h3>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-content-3" />
              <div className="flex rounded-lg border border-line overflow-hidden">
                {/* Cada pestaña lleva su conteo: es lo que se quiere ver de un vistazo
                    («¿a cuántos les llegó ya?») sin ir pestaña por pestaña. */}
                {QUEUE_FILTERS.map((o) => (
                  <button key={o.value} onClick={() => setQueueFilter(o.value)} disabled={queueLoading}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                      queueFilter === o.value ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 font-semibold' : 'bg-surface text-content-2 hover:bg-surface-2'
                    }`}>
                    {o.label}
                    <span className="ml-1 opacity-70">{conteos[o.value] ?? 0}</span>
                  </button>
                ))}
              </div>
              <button onClick={loadQueue} disabled={queueLoading} title="Refrescar"
                className="p-1.5 rounded-md border border-line text-content-3 hover:bg-surface-2 disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${queueLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="text-sm text-content-3 mb-3 flex items-start gap-2">
              <Send className="h-4 w-4 mt-0.5 shrink-0" />
              {planIsActive
                ? <span>Lista real del envío: cada quien con el estado en que quedó.</span>
                : <span>Todavía no se programa el envío: esta es la gente que lo <b>recibiría</b>,
                    calculada hoy. Se vuelve a calcular al programar, así que puede cambiar si
                    entran o salen clientes del criterio.</span>}
            </div>
            {/* Sentido del histórico que NO está en esta base: las campañas anteriores al
                cambio de sistema (jun-2026) conservan su contador pero no la lista de a
                quién le llegó — se enviaban desde el navegador y ese detalle nunca vivió
                aquí. Sin este aviso el panel parece roto: «88 enviados» y la lista vacía. */}
            {!planIsActive && sentCount > 0 && conteos.sent === 0 && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                Esta campaña ya mandó <b>{sentCount}</b> mensajes, pero fue con el sistema anterior
                (antes de junio de 2026) y el detalle de <b>a quién</b> no se guardaba aquí: solo
                quedó el total. Lo que ves abajo es a quiénes les llegaría si la vuelves a enviar.
              </div>
            )}
            {quota && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                {esAsistida
                  ? 'Los mandas tú: pulsa «Enviar» en cada pendiente. '
                  : 'Se mandan solos, pero puedes empujar a alguien con «Enviar». '}
                Hoy este WhatsApp lleva{' '}
                <span className="font-semibold">{quota.used_today ?? quota.sent_today} de {quota.day_cap}</span>
                {quota.used_today != null && quota.used_today !== quota.sent_today
                  ? <> ({quota.sent_today} de esta campaña)</> : null}
                {quota.pace_reason ? <> · {quota.pace_reason}</> : null}.
                {motivoDeFreno(quota) && (
                  <span className="block mt-1 font-medium">{motivoDeFreno(quota)}</span>
                )}
              </div>
            )}
            {queueLoading && queue.length === 0 ? (
              <div className="text-center py-6"><RefreshCw className="h-8 w-8 animate-spin text-content-3 mx-auto" /></div>
            ) : queueVisible.length === 0 ? (
              <div className="text-center py-6">
                <Users className="h-8 w-8 text-content-3 mx-auto" />
                <p className="mt-2 text-sm text-content-3">
                  {queue.length === 0
                    ? 'Ningún cliente cumple el criterio de esta campaña ahora mismo.'
                    : 'Nadie en este filtro.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[32rem] overflow-y-auto">
                {queueVisible.map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-3 border border-line rounded-lg px-3 py-2 text-sm">
                    <span className="min-w-0">
                      <span className="block truncate text-content font-medium">{nombreDe(q)}</span>
                      <span className="block font-mono text-xs text-content-3">
                        {q.phone_live || q.phone}
                        {/* El CUÁNDO: sin esto la lista dice a quién le llegó pero no en qué
                            momento, que es la mitad de la pregunta. */}
                        {q.sent_at && (
                          <span className="font-sans"> · {new Date(q.sent_at).toLocaleString('es-MX',
                            { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        )}
                        {q.reason ? <span className="font-sans"> · {q.reason}</span> : null}
                        {/* Fatiga: se ve ANTES de pulsar Enviar. Solo avisa; mandar sigue
                            siendo decisión de quien opera (y queda rastro en el servidor). */}
                        {q.status === 'pending' && q.fatiga_campana && (
                          <span className="font-sans text-amber-700 dark:text-amber-400"
                            title={`Ya recibió la campaña «${q.fatiga_campana}» hace ${q.fatiga_hace_dias} días`}>
                            {' '}· ⚠ recibió «{q.fatiga_campana}»{q.fatiga_canal === 'email' ? ' por correo' : ''} hace {q.fatiga_hace_dias} d
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      {q.detail === 'shadow' && <span className="text-[11px] text-content-3" title="Sin WhatsApp configurado: simulado">shadow</span>}
                      {q.detail === 'histórico' && (
                        <span className="text-[11px] text-content-3"
                          title="Se envió con el sistema anterior, antes de junio de 2026">histórico</span>
                      )}
                      {/* Mandar de a uno estaba antes de Inc10c y se perdió: solo quedó en las
                          campañas manuales. El motor nunca lo limitó a esas (`sendAssistedRow`
                          solo rechaza correo, filas no pendientes y descartados), así que vuelve
                          a estar en todas. `q.id > 0` = fila REAL de la cola; en un borrador las
                          filas son de la audiencia prevista y todavía no existen. */}
                      {/* La tacha de la agenda: sacar a alguien de la lista. Va ANTES del
                          botón verde, igual que allá. */}
                      {q.status === 'pending' && (
                        <button onClick={() => setADescartar(q)} title="Descartar"
                          className="p-1.5 rounded-full text-content-3 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                      {/* Deshacer, pero solo donde el motor lo permite:
                          · descartado → siempre (undiscard).
                          · enviado → SOLO si lo mandó una persona (`detail='assisted'`).
                            `unmarkAssistedSent` rechaza el resto a propósito: devolver a
                            pendiente una fila que mandó el worker haría que se la volviera
                            a mandar a esa persona en el siguiente ciclo. El worker escribe
                            'sent'/'shadow', y el histórico rescatado ni siquiera está en la
                            cola. Antes el botón salía en todas y siempre fallaba. */}
                      {(q.status === 'discarded' || (q.status === 'sent' && q.detail === 'assisted')) && (
                        <button onClick={() => deshacer(q)} title="Deshacer"
                          className="p-1.5 rounded-full text-content-3 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors">
                          <Undo2 className="h-4 w-4" />
                        </button>
                      )}
                      {!isEmail && q.status === 'pending' && (
                        <button onClick={() => enviarFila(q.id, q.contact_id)}
                          disabled={enviandoId !== null || envioBloqueado !== null || (quota != null && quota.remaining <= 0)}
                          title={envioBloqueado || (quota && quota.remaining <= 0 ? 'Sin cupo por ahora' : 'Enviar este mensaje ahora')}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed">
                          {enviandoId === q.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          Enviar
                        </button>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        q.status === 'sent' ? 'bg-green-50 text-green-700 border-green-200'
                          : q.status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                          : q.status === 'cancelled' || q.status === 'discarded' ? 'bg-canvas text-content-3 border-line'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>
                        {q.status === 'sent' ? 'Enviado' : q.status === 'failed' ? 'Fallido'
                          : q.status === 'cancelled' ? 'Cancelado'
                          : q.status === 'discarded' ? 'Descartado' : 'Pendiente'}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
            {planIsActive && plan?.status === 'paused_error' && (
              <div className="mt-4 flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                Los fallidos se reintentan automáticamente al reanudar.
              </div>
            )}
          </div>
        </div>
      )}

      {busy && (
        <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center z-50">
          <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
          <span className="text-sm font-medium text-content-2">Procesando…</span>
        </div>
      )}
    </div>
  );
};

export default CampaignPreview;
